/**
 * AddManualInvoiceModal - Modal para añadir facturas manuales
 *
 * Permite crear facturas manuales para equipos 3PL con opción de descuento inmediato
 * o diferido. Incluye subida opcional de archivo PDF.
 */

import React from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInvoiceModalTeamsCombobox } from '../../hooks/useInvoiceModalTeamsCombobox';
import { useCreateManualInvoice, CreateManualInvoiceData } from '../../hooks/useCreateManualInvoice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faTrash, faUpload } from '@fortawesome/pro-regular-svg-icons';

export interface AddManualInvoiceModalRef {
  open: (onSuccess?: () => void) => void;
}

interface ManualInvoiceFormData {
  team_id: string;
  erp_number: string;
  date: string;
  base_total: string;
  taxes_total: string;
  grand_total: string;
  expired_at: string;
  pdf_file: File | null;
}

export const AddManualInvoiceModal = forwardRef<AddManualInvoiceModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { searchFn: teamSearchFn, onLoadMore: loadMoreTeams, hasMore: hasMoreTeams, isLoadingBrowse: isLoadingTeamsBrowse } =
    useInvoiceModalTeamsCombobox({ enabled: isModalOpen });

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const { createManualInvoice, isCreating } = useCreateManualInvoice();
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ManualInvoiceFormData>({
    defaultValues: {
      team_id: '',
      erp_number: '',
      date: '',
      base_total: '',
      taxes_total: '',
      grand_total: '',
      expired_at: '',
      pdf_file: null,
    },
  });

  const watchedPdfFile = watch('pdf_file');
  const watchedExpiredAt = watch('expired_at');

  useImperativeHandle(ref, () => ({
    open: (onSuccess?: () => void) => {
      setOnSuccessCallback(() => onSuccess || null);
      setIsModalOpen(true);
      reset();
      modalRef.current?.openModal();
    },
  }));

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setValue('pdf_file', file);
  };

  const handleRemoveFile = () => {
    setValue('pdf_file', null);
    // Reset the input
    const fileInput = document.getElementById('pdf_file_input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const calculateGrandTotal = () => {
    const baseTotal = parseFloat(watch('base_total')) || 0;
    const taxesTotal = parseFloat(watch('taxes_total')) || 0;
    const grandTotal = baseTotal + taxesTotal;

    setValue('grand_total', grandTotal.toFixed(2));
  };

  const onSubmit = async (data: ManualInvoiceFormData) => {
    const invoiceData: CreateManualInvoiceData = {
      team_id: parseInt(data.team_id),
      erp_number: data.erp_number,
      date: data.date,
      base_total: parseFloat(data.base_total),
      taxes_total: parseFloat(data.taxes_total),
      grand_total: parseFloat(data.grand_total),
      pdf_file: data.pdf_file || undefined,
      expired_at: data.expired_at || undefined,
    };

    const result = await createManualInvoice(invoiceData, (response) => {
      console.log('Factura creada exitosamente:', response);
    });

    if (result) {
      onSuccessCallback?.();
      modalRef.current?.closeModal();
    }
  };

  const handleCancel = () => {
    modalRef.current?.closeModal();
  };

  // Valida montos numéricos permitiendo valores negativos y coma decimal
  const validateAmount = (value: string) => {
    if (value === null || value === undefined || value.toString().trim() === '') {
      return false;
    }
    const normalized = value.toString().replace(',', '.');
    return Number.isFinite(Number(normalized));
  };

  // Calcular automáticamente el total cuando cambien base_total o taxes_total
  React.useEffect(() => {
    calculateGrandTotal();
  }, [watch('base_total'), watch('taxes_total')]);

  return (
    <ModalCenter
      ref={modalRef}
      type="default"
      title={t('modals.add_manual_invoice.title', 'Añadir Factura Manual')}
      width="lg"
      onCloseModal={handleCloseModal}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Equipo */}
        <div className="space-y-2">
          <Label htmlFor="team_id">{t('modals.add_manual_invoice.team', 'Equipo')} *</Label>
          <Controller
            name="team_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DynamicCombobox
                value={field.value}
                setValue={(v) => field.onChange(v)}
                placeholder={
                  isLoadingTeamsBrowse
                    ? t('common.loading', 'Cargando...')
                    : t('modals.add_manual_invoice.select_team', 'Seleccionar equipo')
                }
                searchFn={teamSearchFn}
                onLoadMore={loadMoreTeams}
                hasMore={hasMoreTeams}
                disabled={isLoadingTeamsBrowse}
                dataTestId="add-manual-invoice-client-combobox"
                emptyMessage={t('modals.add_manual_invoice.no_clients_found', 'No se encontraron clientes')}
              />
            )}
          />
          {errors.team_id && (
            <p className="text-sm text-destructive">{t('validation.team_required', 'Equipo requerido')}</p>
          )}
        </div>

        {/* Número ERP */}
        <div className="space-y-2">
          <Label htmlFor="erp_number">{t('modals.add_manual_invoice.erp_number', 'Número ERP')} *</Label>
          <Input
            id="erp_number"
            {...register('erp_number', { required: true })}
            placeholder={t('modals.add_manual_invoice.erp_number_placeholder', 'Ej: INV-001')}
          />
          {errors.erp_number && (
            <p className="text-sm text-destructive">{t('validation.erp_number_required', 'Número ERP requerido')}</p>
          )}
        </div>

        {/* Fecha */}
        <div className="space-y-2">
          <Label htmlFor="date">{t('modals.add_manual_invoice.date', 'Fecha')} *</Label>
          <Input id="date" type="date" {...register('date', { required: true })} />
          {errors.date && (
            <p className="text-sm text-destructive">{t('validation.date_required', 'Fecha requerida')}</p>
          )}
        </div>

        {/* Montos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="base_total">{t('modals.add_manual_invoice.base_total', 'Base')} *</Label>
            <Input
              id="base_total"
              type="number"
              step="0.01"
              selectAllOnFocus={false}
              onFocus={(e) => e.currentTarget.select()}
              {...register('base_total', {
                validate: (value) => validateAmount(value),
              })}
              placeholder="0.00"
            />
            {errors.base_total && (
              <p className="text-sm text-destructive">{t('validation.base_total_required', 'Base requerida')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="taxes_total">{t('modals.add_manual_invoice.taxes_total', 'Impuestos')} *</Label>
            <Input
              id="taxes_total"
              type="number"
              step="0.01"
              selectAllOnFocus={false}
              onFocus={(e) => e.currentTarget.select()}
              {...register('taxes_total', {
                validate: (value) => validateAmount(value),
              })}
              placeholder="0.00"
            />
            {errors.taxes_total && (
              <p className="text-sm text-destructive">{t('validation.taxes_total_required', 'Impuestos requeridos')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="grand_total">{t('modals.add_manual_invoice.grand_total', 'Total')} *</Label>
            <Input
              id="grand_total"
              type="number"
              step="0.01"
              {...register('grand_total', {
                validate: (value) => validateAmount(value),
              })}
              placeholder="0.00"
              readOnly
              className="bg-muted"
            />
            {errors.grand_total && (
              <p className="text-sm text-destructive">{t('validation.grand_total_required', 'Total requerido')}</p>
            )}
          </div>
        </div>

        {/* Fecha de expiración */}
        <div className="space-y-2">
          <Label htmlFor="expired_at">{t('modals.add_manual_invoice.expired_at', 'Fecha de expiración')}</Label>
          <Input id="expired_at" type="date" {...register('expired_at')} />
          <p className="text-sm text-complementary-text">
            {watchedExpiredAt
              ? t(
                  'modals.add_manual_invoice.expired_at_help_with_date',
                  'Se cobrará cuando llegue la fecha de expiración',
                )
              : t('modals.add_manual_invoice.expired_at_help', 'Si no se especifica, se cobrará inmediatamente')}
          </p>
        </div>

        {/* Archivo PDF */}
        <div className="space-y-2">
          <Label htmlFor="pdf_file">{t('modals.add_manual_invoice.pdf_file', 'Archivo PDF')}</Label>
          <div className="space-y-2">
            {!watchedPdfFile ? (
              <div className="border-2 border-dashed border-card-border rounded-lg p-4 text-center hover:border-primary transition-colors">
                <input id="pdf_file_input" type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />
                <label htmlFor="pdf_file_input" className="cursor-pointer flex flex-col items-center space-y-2">
                  <FontAwesomeIcon icon={faUpload} className="text-2xl text-complementary-text" />
                  <span className="text-sm text-complementary-text">
                    {t('modals.add_manual_invoice.pdf_file_placeholder', 'Haz clic para seleccionar PDF (máx. 5MB)')}
                  </span>
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-card-bg border border-card-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFilePdf} className="text-red-500" />
                  <div>
                    <p className="text-sm font-medium">{watchedPdfFile.name}</p>
                    <p className="text-xs text-complementary-text">
                      {(watchedPdfFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  className="text-destructive hover:text-destructive"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 pt-6 border-t border-card-border">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isCreating}>
            {t('modals.add_manual_invoice.cancel', 'Cancelar')}
          </Button>
          <Button type="submit" disabled={isCreating}>
            {isCreating
              ? t('modals.add_manual_invoice.creating', 'Creando...')
              : t('modals.add_manual_invoice.create', 'Crear Factura')}
          </Button>
        </div>
      </form>
    </ModalCenter>
  );
});

AddManualInvoiceModal.displayName = 'AddManualInvoiceModal';
